import { ArrowRight, CheckCircle2 } from "lucide-react";

// ──────────────────────────────────────────────────────────────────────────
// article-kit.tsx
// Shared primitives for long-form Insights articles. Every cited analysis
// (PriorityZipCodesArticle, BlackBusinessGapArticle, UnbankedUnderbankedArticle,
// future ones) imports these so structure, citation behavior, and styling
// stay consistent. See _TEMPLATE.md in this folder for authoring guidance.
// ──────────────────────────────────────────────────────────────────────────

export type Source = { n: number; label: string; url: string };

/** Inline citation superscript linking to the Sources list at the bottom. */
export const Cite = ({ ns }: { ns: number[] }) => (
  <sup className="ml-0.5 text-[10px] font-mono font-medium tracking-tight">
    {ns.map((n, i) => (
      <span key={n}>
        {i > 0 && <span className="text-muted-foreground">,</span>}
        <a
          href={`#source-${n}`}
          className="text-primary hover:underline"
          aria-label={`Jump to source ${n}`}
        >
          [{n}]
        </a>
      </span>
    ))}
  </sup>
);

/** Numbered, anchored sources list rendered at the bottom of every article. */
export const SourcesList = ({ sources }: { sources: Source[] }) => (
  <section className="border-t border-border pt-8">
    <h2 className="font-display text-xl font-bold text-foreground mb-4">Sources</h2>
    <ol className="space-y-2 text-sm">
      {sources.map((s) => (
        <li
          key={s.n}
          id={`source-${s.n}`}
          className="flex gap-3 text-muted-foreground scroll-mt-24 target:bg-primary/5 rounded-md px-1"
        >
          <span className="text-primary font-mono shrink-0">[{s.n}]</span>
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary hover:underline break-words"
          >
            {s.label}
          </a>
        </li>
      ))}
    </ol>
  </section>
);

/** Recharts tooltip style aligned to our dark theme. */
export const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "12px",
  color: "hsl(var(--foreground))",
};

/** Optional kicker + h2 pair used to open each major section. */
export const SectionHeader = ({
  kicker,
  title,
  description,
}: {
  kicker?: string;
  title: string;
  description?: string;
}) => (
  <div className="mb-5">
    {kicker && (
      <span className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
        {kicker}
      </span>
    )}
    <h2
      className={`font-display text-2xl md:text-3xl font-bold text-foreground ${
        kicker ? "mt-2" : ""
      } ${description ? "mb-2" : "mb-0"}`}
    >
      {title}
    </h2>
    {description && (
      <p className="text-muted-foreground leading-relaxed max-w-2xl">{description}</p>
    )}
  </div>
);

export type IconListItem = {
  lead: string;
  body: React.ReactNode;
};

/**
 * Bulleted list with bold lead phrase + supporting body. Use `variant="arrow"`
 * for "Why X persists" style sections; `variant="check"` for "What to do" lists.
 */
export const IconList = ({
  items,
  variant = "arrow",
}: {
  items: IconListItem[];
  variant?: "arrow" | "check";
}) => {
  const Icon = variant === "check" ? CheckCircle2 : ArrowRight;
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <Icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <span className="text-muted-foreground">
            <strong className="text-foreground">{it.lead}</strong> {it.body}
          </span>
        </li>
      ))}
    </ul>
  );
};

/** Standard chart container used for every recharts visualization. */
export const ChartCard = ({
  title,
  subtitle,
  children,
  footnote,
  sources,
  height = 260,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footnote?: string;
  sources?: number[];
  height?: number;
}) => (
  <div className="bg-card border border-border rounded-xl p-5">
    <h4 className="font-display font-bold text-base text-foreground">
      {title}
      {sources && sources.length > 0 && <Cite ns={sources} />}
    </h4>
    {subtitle && <p className="text-xs text-muted-foreground mt-1 mb-4">{subtitle}</p>}
    <div style={{ height }} className="w-full">
      {children}
    </div>
    {footnote && <p className="text-[11px] text-muted-foreground mt-3 italic">{footnote}</p>}
  </div>
);